import { Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { withRouter } from "./hooks/withRouter";
import ErrorBoundary from "./ErrorBoundary";
import Home2 from "./containers/Home2";
import DeveloperLanding from "./containers/DeveloperLanding";
import Overview from "./containers/Overview";
import Body from "./containers/Body";
import Login from "./containers/authentication/Login";
import Profile from "./containers/components/Profile";
import Devices from "./containers/components/Devices";
import RemoteSSH from "./containers/components/RemoteSSH";
import ProtectedDownloads from "./containers/components/ProtectedDownloads";
import ProtectedRoute from "./ProtectedRoute";
import DocsPage from "./containers/components/docs/DocsPage";
import { Downloads } from "./containers/components/downloads/Downloads";
import LogAnalyzer from "./containers/components/logAnalyzer/LogAnalyzer";
import LogDetailPage from "./containers/components/logAnalyzer/LogDetailPage";
import PublicLogAnalyzer from "./containers/components/logAnalyzer/PublicLogAnalyzer";
import PublicLogDetail from "./containers/components/logAnalyzer/PublicLogDetail";
import PublicShell from "./containers/components/logAnalyzer/PublicShell";
import ComparisonPage from "./containers/components/logAnalyzer/ComparisonPage";
import FirmwareFlasher from "./containers/components/firmware/FirmwareFlasher";
import PublicDownloads from "./containers/components/downloads/PublicDownloads";
import Training from "./containers/components/training/Training";
import PublicDownloadDetail from "./containers/components/downloads/PublicDownloadDetail";
import PropulsionCalc from "./containers/components/propulsionCalc/PropulsionCalc";
import SimulatorPage from "./containers/components/propulsionCalc/SimulatorPage";
import PropulsionComparePage from "./containers/components/propulsionCalc/PropulsionComparePage";
import PropulsionBuilderPage from "./containers/components/propulsionCalc/BuilderPage";
import PropulsionCatalogPage from "./containers/components/propulsionCalc/CatalogPage";
import PublicPropulsion from "./containers/components/propulsionCalc/PublicPropulsion";
import MotorDataIndex from "./containers/components/motorData/MotorDataIndex";
import MotorDataDetail from "./containers/components/motorData/MotorDataDetail";
import MotorDetail from "./containers/components/motorData/MotorDetail";
import ThrustStandApp from "./thrustStand/App";
import NotFound from "./containers/NotFound";
import { ROUTES } from "./routes";
import { PUBLIC_BASE as LOG_PUBLIC_BASE } from "./containers/components/logAnalyzer/paths";
import { PUBLIC_BASE as PROPULSION_PUBLIC_BASE } from "./containers/components/propulsionCalc/paths";
import { DOWNLOADS_PUBLIC_PATH } from "./containers/components/downloads/catalog";
import { PUBLIC_BASE as MOTOR_PUBLIC_BASE } from "./containers/components/motorData/catalog";

/** Route-level fallback for a render throw the page itself didn't catch. */
const RouteError = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 8, px: 2, textAlign: "center" }}>
    <Typography variant="h6">Something went wrong</Typography>
    <Typography variant="body2" color="text.secondary">
      This page hit an unexpected error. Reloading usually fixes it.
    </Typography>
    <Button variant="contained" onClick={() => window.location.reload()}>
      Reload page
    </Button>
  </Box>
);

const BaseRouter = (props) => {
  const { navigate } = props;
  const location = useLocation();
  const [data, setData] = useState(location?.state);
  return (
    <div>
      <ErrorBoundary fallback={<RouteError />}>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute navigate={navigate} data={data} setData={setData}>
              <Body {...props} />
            </ProtectedRoute>
          }
        >
          <Route path="overview" element={<Navigate to={ROUTES.overview} replace />} />
          <Route path="profile">
            <Route index element={<Profile {...props} />} />
          </Route>
          <Route path="services">
            <Route index element={<Navigate to={ROUTES.dashboard} replace />} />
            <Route path="developer">
              <Route index element={<Navigate to={ROUTES.dashboard} replace />} />
              <Route path="dashboard" element={<Home2 {...props} />} />
              <Route path="overview" element={<Overview {...props} />} />
              <Route path="docspage">
                <Route index element={<DocsPage {...props} />} />
              </Route>
              <Route path="downloads">
                <Route index element={<Downloads {...props} />} />
              </Route>
              <Route path="devices">
                <Route index element={<Devices {...props} />} />
                <Route path=":id">
                  <Route index element={<Devices {...props} />} />
                  <Route path="remoteSSH">
                    <Route index element={<RemoteSSH {...props} />} />
                  </Route>
                </Route>
              </Route>
              <Route path="remoteSSH">
                <Route index element={<RemoteSSH {...props} />} />
              </Route>
              <Route path="protected-downloads">
                <Route index element={<DocsPage {...props} />} />
                <Route path=":id" element={<ProtectedDownloads {...props} />} />
              </Route>
              <Route path="thrust-stand/*" element={<ThrustStandApp />} />
              <Route path="log-analyzer">
                <Route index element={<LogAnalyzer {...props} />} />
                <Route path="compare/:id" element={<ComparisonPage {...props} />} />
                <Route path=":id" element={<LogDetailPage {...props} />} />
              </Route>
              <Route path="firmware-flasher">
                <Route index element={<FirmwareFlasher {...props} />} />
              </Route>
              {/* Signed-in view of the same pages: app chrome, and every gate
                  open (full throttle sweep, methodology, whole library). */}
              <Route path="motor-data">
                <Route index element={<MotorDataIndex {...props} />} />
                {/* Static segment before the dynamic one: `motor/x` is a single
                    motor, a bare slug is a claimed-vs-measured combination. */}
                <Route path="motor/:slug" element={<MotorDetail {...props} />} />
                <Route path=":slug" element={<MotorDataDetail {...props} />} />
              </Route>
              <Route path="propulsion-calculator">
                <Route index element={<PropulsionCalc {...props} />} />
                {/* /new = the rich live calculator (same component as the public
                    page, auth-aware); /new/classic = backend-catalog builder,
                    the flow that can SAVE combinations. */}
                <Route path="new" element={<PublicPropulsion {...props} />} />
                <Route path="new/classic" element={<PropulsionBuilderPage {...props} />} />
                <Route path="catalog" element={<PropulsionCatalogPage {...props} />} />
                <Route path="compare" element={<PropulsionComparePage {...props} />} />
                <Route path=":id" element={<SimulatorPage {...props} />} />
              </Route>
            </Route>
          </Route>
        </Route>
        {/* The developer homepage. Same page ProtectedRoute shows logged-out
            visitors at "/", but reachable signed in too — the header then
            offers "Access the app" instead of sign-in. */}
        <Route path={ROUTES.publicHome} element={<DeveloperLanding {...props} />} />

        {/* Public tool pages — minimal shell, no app chrome (logged-out landing). */}
        <Route path={LOG_PUBLIC_BASE} element={<PublicShell {...props} />}>
          <Route index element={<PublicLogAnalyzer {...props} />} />
          <Route path=":id" element={<PublicLogDetail {...props} />} />
        </Route>

        <Route
          path={DOWNLOADS_PUBLIC_PATH}
          element={
            <PublicShell
              {...props}
              appPath={ROUTES.downloads}
              seoTitle="Developer Downloads — Vecros"
              seoDesc="Firmware, release bundles and hardware assets for building on the Vecros developer platform. Browse the catalog free — sign in to pull a file."
            />
          }
        >
          <Route index element={<PublicDownloads {...props} />} />
          <Route path=":slug" element={<PublicDownloadDetail {...props} />} />
        </Route>

        <Route
          path={PROPULSION_PUBLIC_BASE}
          element={
            <PublicShell
              {...props}
              accent="#006BCE"
              appPath={ROUTES.propulsionCalc}
              seoTitle="Size your propulsion — Vecros"
              seoDesc="Simulate a motor + propeller + ESC + battery combination for thrust, flight time and TWR — or explore setups the community has shared. Free, no sign-up to browse."
            />
          }
        >
          <Route index element={<PublicPropulsion {...props} />} />
        </Route>

        <Route
          path={ROUTES.training}
          element={
            <PublicShell
              {...props}
              appPath={ROUTES.developer}
              seoTitle="Drone AI Training & Certification — Vecros"
              seoDesc="Hands-on courses that take you from training a model to deploying it onboard a drone — real flight data, real hardware, and a verifiable Vecros certificate."
            />
          }
        >
          <Route index element={<Training {...props} />} />
        </Route>

        {/* Motor & propeller test data — manufacturer claim vs our bench
            measurement. */}
        <Route
          path={MOTOR_PUBLIC_BASE}
          element={
            <PublicShell
              {...props}
              accent="#006BCE"
              appPath={ROUTES.motorData}
              seoTitle="Motor & propeller test data — claimed vs measured | Vecros"
              seoDesc="What manufacturers publish, and what the motor actually did on our thrust stand. Thrust, current, efficiency, torque and temperature at every throttle point."
            />
          }
        >
          <Route index element={<MotorDataIndex {...props} />} />
          <Route path="motor/:slug" element={<MotorDetail {...props} />} />
          <Route path=":slug" element={<MotorDataDetail {...props} />} />
        </Route>

        <Route path="auth">
          <Route path="login" element={<Login {...props} />} />
        </Route>

        {/* Last: any URL that matched nothing above, so a mistyped or
            truncated link gets a real page instead of an empty #root. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </ErrorBoundary>
    </div>
  );
};

export default withRouter(BaseRouter);
